import type { ForecastDay } from "@/types/weather";
import type { ForecastHour } from "@shared/forecast";

export interface ForecastSnapshot {
  label: "6 AM" | "Noon" | "6 PM";
  swell: string;
  wind: string;
}

const SNAPSHOT_TIMES = [
  { label: "6 AM", hour: 6 },
  { label: "Noon", hour: 12 },
  { label: "6 PM", hour: 18 },
] as const;
const COMPASS = ["N", "NNE", "NE", "ENE", "E", "ESE", "SE", "SSE", "S", "SSW", "SW", "WSW", "W", "WNW", "NW", "NNW"];

function measurement(value: unknown, max: number): number | null {
  return typeof value === "number" && Number.isFinite(value) && value >= 0 && value <= max ? value : null;
}
function compass(value: unknown): string | null {
  const direction = measurement(value, 360);
  return direction === null ? null : COMPASS[Math.round(direction / 22.5) % 16];
}

/** Exact local clock hours only. A missing slot is never a nearest-hour guess. */
export function getForecastSnapshots(day: Pick<ForecastDay, "dateKey" | "timezone" | "hourly" | "fetchedAt">): ForecastSnapshot[] {
  const unavailable = () => SNAPSHOT_TIMES.map(({ label }) => ({ label, swell: "Unavailable", wind: "Unavailable" }));
  if (!day.timezone || !/^\d{4}-\d{2}-\d{2}$/.test(day.dateKey) || !Array.isArray(day.hourly)) return unavailable();
  let clock: Intl.DateTimeFormat;
  try {
    clock = new Intl.DateTimeFormat("en-CA", { timeZone: day.timezone, year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", second: "2-digit", hourCycle: "h23" });
  } catch { return unavailable(); }
  const slots = new Map<number, ForecastHour[]>();
  const fetched = day.fetchedAt === undefined ? null : Date.parse(day.fetchedAt);
  for (const point of day.hourly) {
    if (!point || typeof point.validAt !== "string" ||
      !/^\d{4}-\d{2}-\d{2}T(?:[01]\d|2[0-3]):[0-5]\d:[0-5]\d(?:\.\d{3})?Z$/.test(point.validAt)) continue;
    const valid = Date.parse(point.validAt);
    if (!Number.isFinite(valid) || new Date(valid).toISOString().slice(0, 19) !== point.validAt.slice(0, 19) ||
      valid % 1000 !== 0 || (fetched !== null && (!Number.isFinite(fetched) || valid <= fetched || valid > fetched + 168 * 3_600_000))) continue;
    const p = Object.fromEntries(clock.formatToParts(new Date(valid)).map(part => [part.type, part.value]));
    const date = `${p.year}-${p.month}-${p.day}`;
    const hour = Number(p.hour);
    if (date !== day.dateKey || p.minute !== "00" || p.second !== "00" ||
      !SNAPSHOT_TIMES.some(slot => slot.hour === hour)) continue;
    slots.set(hour, [...(slots.get(hour) ?? []), point]);
  }
  return SNAPSHOT_TIMES.map(({ label, hour }) => {
    const matches = slots.get(hour);
    // Ambiguous duplicate timestamps must not silently choose different values.
    if (!matches || matches.length !== 1) return { label, swell: "Unavailable", wind: "Unavailable" };
    const point = matches[0];
    const height = measurement(point.waveHeightFt, 100), period = measurement(point.wavePeriodSec, 40);
    const waveDirection = compass(point.waveDirectionDeg), speed = measurement(point.windSpeedMph, 200);
    const windDirection = compass(point.windDirectionDeg);
    const swell = height === null && period === null && waveDirection === null ? "Unavailable" :
      `${height === null ? "Height unavailable" : `${height.toFixed(1)} ft`} - ${period === null ? "Period unavailable" : `${period.toFixed(0)}s`} - ${waveDirection ?? "Direction unavailable"}`;
    const wind = speed === null && windDirection === null ? "Unavailable" :
      `${speed === null ? "Speed unavailable" : `${speed.toFixed(0)} mph`} - ${windDirection ?? "Direction unavailable"}`;
    return { label, swell, wind };
  });
}

export function detailedForecastPath(locationId: number, dayOffset: number): string {
  if (!Number.isInteger(locationId) || locationId <= 0) {
    throw new Error("A valid location is required for forecast details.");
  }
  if (!Number.isInteger(dayOffset) || dayOffset < 0 || dayOffset > 7) {
    throw new Error("Forecast day offset must be an available calendar-day offset.");
  }
  return `/api/locations/${locationId}/detailed-forecast/${dayOffset}`;
}

export function formatForecastDateTime(value?: string | null, timezone?: string): string {
  if (!value) return "Unavailable";
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return "Unavailable";
  try {
    return date.toLocaleString("en-US", {
      timeZone: timezone || "UTC",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      timeZoneName: "short",
    });
  } catch {
    return date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      timeZoneName: "short",
      timeZone: "UTC",
    });
  }
}
